import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { btnP, btnSec, rad, sp, type } from "../../constants/styles";
import Ic from "../common/Ic";
import Fld from "../common/Fld";
import { LANGUAGES } from "../../i18n";
import TermsView from "../common/TermsView";
import PrivacyView from "../common/PrivacyView";
import { TERMS_VERSION } from "../../utils/terms";

const GSI_SRC = "https://accounts.google.com/gsi/client";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

export default function LoginPage() {
  const { t, h, lang, setLang, dk, setDk, role, setRole, setLoggedIn, setCurrUserId, setPublicPage, setOrgId } = useContext(Ctx);
  const [ph, setPh] = useState("");
  const [pwd, setPwd] = useState("");
  const [name, setName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [skill, setSkill] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [mode, setMode] = useState("login"); // login | signup | googleRole
  const [loading, setLoading] = useState(false);
  const [loginErr, setLoginErr] = useState("");
  // ── Forgot Password (OTP flow) ──
  // Step 1: phone → server sends OTP via WhatsApp
  // Step 2: enter OTP + new password → server verifies + resets
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1=phone, 2=otp+newPwd
  const [forgotPh, setForgotPh] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPwd, setForgotNewPwd] = useState("");
  const [forgotConfirm, setForgotConfirm] = useState("");
  const [forgotErr, setForgotErr] = useState("");
  const [forgotInfo, setForgotInfo] = useState("");  // generic confirmation banner
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [otpResendIn, setOtpResendIn] = useState(0); // seconds remaining before resend allowed
  // Tick down resend timer
  React.useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);
  // Terms acceptance — required before signup. Hard-blocks the signup
  // button until checked. The accepted version is sent to /api/signup
  // so the server can record consent in `terms_acceptances` (legal audit).
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const [tenancies, setTenancies] = useState(null); // multi-org picker
  const [tenancyPh, setTenancyPh] = useState(""); // store phone for org selection
  const [tenancyPwd, setTenancyPwd] = useState(""); // store pwd for org selection
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleCredential, setGoogleCredential] = useState(null);
  const [googleUser, setGoogleUser] = useState(null);
  const googleBtnRef = useRef(null);
  const gsiLoaded = useRef(false);

  const completeLogin = useCallback((resRole, res) => {
    localStorage.setItem("loggedIn", "true");
    localStorage.setItem("role", resRole);
    if (res.orgId) localStorage.setItem("orgId", res.orgId);
    if (res.user?.id) { localStorage.setItem("currUserId", res.user.id); setCurrUserId(res.user.id); }
    localStorage.setItem("googleUser", JSON.stringify(res.user));
    if (setOrgId && res.orgId) setOrgId(res.orgId);
    setRole(resRole);
    setLoggedIn(true);
  }, [setCurrUserId, setRole, setLoggedIn, setOrgId]);

  const handleGoogleResponse = useCallback(async (response) => {
    if (!response.credential) return;
    setGoogleLoading(true);
    setLoginErr("");
    try {
      const res = await api.googleLogin(response.credential);
      if (res.success && res.needsRole) {
        // New Google user — needs to pick a role and provide phone
        setGoogleCredential(response.credential);
        setGoogleUser(res.user);
        setName(res.user.name || "");
        setMode("googleRole");
        setRole(null);
      } else if (res.success && res.role) {
        // Existing user with a role — auto-login
        completeLogin(res.role, res);
      } else {
        setLoginErr(res.message || "Google sign-in failed");
      }
    } catch (err) {
      setLoginErr("Google sign-in failed: " + err.message);
    } finally {
      setGoogleLoading(false);
    }
  }, [completeLogin]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || gsiLoaded.current) return;
    const loadGsi = () => {
      if (window.google?.accounts?.id) { initGsi(); return; }
      if (document.querySelector(`script[src="${GSI_SRC}"]`)) return;
      const script = document.createElement("script");
      script.src = GSI_SRC; script.async = true; script.defer = true;
      script.onload = () => initGsi();
      document.head.appendChild(script);
    };
    const initGsi = () => {
      if (gsiLoaded.current || !window.google?.accounts?.id) return;
      gsiLoaded.current = true;
      window.google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID, callback: handleGoogleResponse, auto_select: false, cancel_on_tap_outside: true });
      renderGoogleButton();
    };
    loadGsi();
  }, [handleGoogleResponse]);

  useEffect(() => {
    if (gsiLoaded.current && googleBtnRef.current && !role) renderGoogleButton();
  }, [dk, role]);

  const renderGoogleButton = () => {
    if (!googleBtnRef.current || !window.google?.accounts?.id) return;
    googleBtnRef.current.innerHTML = "";
    window.google.accounts.id.renderButton(googleBtnRef.current, { type: "standard", theme: dk ? "filled_black" : "outline", size: "large", width: googleBtnRef.current.offsetWidth || 340, text: "continue_with", shape: "pill", logo_alignment: "left" });
  };

  const roleIcons = { landlord: "shield", tenant: "users", service: "wrench", admin: "gear" };
  const roleColors = { landlord: h.ac, tenant: h.ac, service: h.ac, admin: h.pp || h.wn };
  const roleLabels = { landlord: t.landlord, tenant: t.tenant, service: t.servicePerson, admin: t.superAdmin || "Super Admin" };

  const resetForm = () => { setPh(""); setPwd(""); setName(""); setOrgName(""); setSkill(""); setConfirmPwd(""); setLoginErr(""); };
  const switchMode = (m) => { setMode(m); resetForm(); };

  const handleLogin = async () => {
    if (ph.length < 10) { setLoginErr(t.validPhone || "Enter valid phone"); return; }
    if (!pwd) { setLoginErr(t.enterPwd || "Enter password"); return; }
    setLoading(true); setLoginErr("");
    try {
      const res = await api.login(role, ph, pwd);
      if (res.success && res.needsOrgSelection) {
        // Multi-org tenant — show org picker
        setTenancies(res.tenancies);
        setTenancyPh(ph);
        setTenancyPwd(pwd);
      } else if (res.success) {
        localStorage.setItem("loggedIn", "true");
        localStorage.setItem("role", role);
        if (res.orgId) localStorage.setItem("orgId", res.orgId);
        if (role === "tenant" || role === "service") {
          localStorage.setItem("currUserId", res.user.id);
          setCurrUserId(res.user.id);
        } else if (role === "admin") {
          localStorage.setItem("currUserId", "super_admin");
          setCurrUserId("super_admin");
        } else if (role === "landlord" && res.user?.id) {
          localStorage.setItem("currUserId", res.user.id);
          setCurrUserId(res.user.id);
        }
        if (setOrgId && res.orgId) setOrgId(res.orgId);
        setLoggedIn(true);
      } else { setLoginErr(res.message || t.loginFail); }
    } catch (err) { setLoginErr(t.loginFail + ": " + err.message); }
    finally { setLoading(false); }
  };

  const handleSignup = async () => {
    if (!name.trim()) { setLoginErr("Name is required"); return; }
    if (ph.length < 10) { setLoginErr(t.validPhone || "Enter valid phone"); return; }
    if (pwd.length < 4) { setLoginErr("Password must be at least 4 characters"); return; }
    if (pwd !== confirmPwd) { setLoginErr("Passwords don't match"); return; }
    if (role === "landlord" && !orgName.trim()) { setLoginErr("Organization name is required"); return; }
    if (!termsAccepted) { setLoginErr("Please accept the Terms & Conditions to continue"); return; }
    setLoading(true); setLoginErr("");
    try {
      const res = await api.signup(role, ph, pwd, name, orgName, skill, TERMS_VERSION);
      if (res.success) {
        localStorage.setItem("loggedIn", "true");
        localStorage.setItem("role", role);
        if (res.orgId) localStorage.setItem("orgId", res.orgId);
        if (res.user?.id) {
          localStorage.setItem("currUserId", res.user.id);
          setCurrUserId(res.user.id);
        }
        if (setOrgId && res.orgId) setOrgId(res.orgId);
        setLoggedIn(true);
      } else { setLoginErr(res.message || "Signup failed"); }
    } catch (err) { setLoginErr("Signup failed: " + err.message); }
    finally { setLoading(false); }
  };

  const handleGoogleSignup = async (selectedRole) => {
    if (!ph || ph.length < 10) { setLoginErr(t.validPhone || "Enter valid phone"); return; }
    if (selectedRole === "landlord" && !orgName.trim()) { setLoginErr("Organization name is required"); return; }
    if (!termsAccepted) { setLoginErr("Please accept the Terms & Conditions to continue"); return; }
    setLoading(true); setLoginErr("");
    try {
      const res = await api.googleLogin(googleCredential, selectedRole, ph, orgName, skill, name, TERMS_VERSION);
      if (res.success && res.role) {
        completeLogin(res.role, res);
      } else { setLoginErr(res.message || "Signup failed"); }
    } catch (err) { setLoginErr("Signup failed: " + err.message); }
    finally { setLoading(false); }
  };

  const tabS = (active) => ({
    flex: 1, padding: `${sp.sm}px 0`, borderRadius: rad.pill, border: "none",
    background: active ? h.ac : "transparent", color: active ? "#fff" : h.ts,
    fontSize: 13, fontWeight: active ? 600 : 400, cursor: "pointer", transition: "all 0.2s",
  });

  return <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: sp.xxl, maxWidth: 420, margin: "0 auto", background: h.bg }} className="animate-fadeIn">
    {/* Header controls */}
    <div style={{ position: "absolute", top: sp.lg, right: sp.lg, display: "flex", gap: sp.sm }}>
      <button onClick={() => { const codes = LANGUAGES.map(l => l.code); const next = codes[(codes.indexOf(lang) + 1) % codes.length]; setLang(next); }} style={{ background: h.el || h.cd, border: `1px solid ${h.bd}`, color: h.ts || h.sc, padding: "6px 12px", borderRadius: rad.pill, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{(LANGUAGES.find(l => l.code === lang) || LANGUAGES[0]).native}</button>
      <button onClick={() => setDk(!dk)} style={{ background: h.el || h.cd, border: `1px solid ${h.bd}`, padding: "6px 10px", borderRadius: rad.pill, cursor: "pointer", display: "flex", alignItems: "center" }}>{dk ? <Ic n="sun" s={14} c={h.ts || h.sc} /> : <Ic n="moon" s={14} c={h.ts || h.sc} />}</button>
    </div>

    {/* Logo */}
    <div style={{ width: 72, height: 72, borderRadius: 22, background: h.gl || h.ac, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: sp.lg, boxShadow: `0 8px 32px ${h.ac}25` }}><Ic n="bldg" s={36} c="#fff" /></div>

    {/* Title */}
    <h1 style={{ color: h.tx, fontSize: 24, fontWeight: 700, margin: "0 0 6px", letterSpacing: "-0.3px" }}>{t.app}</h1>
    <p style={{ color: h.ts || h.sc, fontSize: 14, margin: "0 0 36px", textAlign: "center", lineHeight: 1.4 }}>{t.tag}</p>

    {/* Org Picker — shown when tenant has multiple tenancies */}
    {tenancies ? <div style={{ width: "100%" }} className="animate-slideUp">
      <button onClick={() => { setTenancies(null); setTenancyPh(""); setTenancyPwd(""); }} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: sp.md, fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}><Ic n="back" s={14} c={h.ac} /> {t.back}</button>
      <div style={{ background: h.cd, borderRadius: rad.lg, padding: sp.xxl, boxShadow: h.sh, border: `1px solid ${h.bd}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.lg }}>
          <div style={{ width: 40, height: 40, borderRadius: rad.md, background: `${h.ac}10`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Ic n="users" s={20} c={h.ac} />
          </div>
          <div>
            <div style={{ color: h.tx, fontWeight: 700, fontSize: 16 }}>{t.selectOrg || "Select Organization"}</div>
            <div style={{ color: h.ts, fontSize: 12 }}>{tenancies.length} {t.tenancies || "tenancies found"}</div>
          </div>
        </div>

        {loginErr && <div style={{ background: `${h.rd}08`, color: h.rd, padding: "10px 14px", borderRadius: rad.md, fontSize: 13, fontWeight: 500, marginBottom: sp.lg, border: `1px solid ${h.rd}15` }}>{loginErr}</div>}

        {tenancies.map(tc => <div key={tc.tenantId}
          onClick={async () => {
            setLoading(true); setLoginErr("");
            try {
              const res = await api.selectOrg(tc.tenantId, tenancyPh, tenancyPwd);
              if (res.success) {
                localStorage.setItem("loggedIn", "true");
                localStorage.setItem("role", "tenant");
                if (res.orgId) localStorage.setItem("orgId", res.orgId);
                localStorage.setItem("currUserId", res.user.id);
                setCurrUserId(res.user.id);
                if (setOrgId && res.orgId) setOrgId(res.orgId);
                setRole("tenant");
                setLoggedIn(true);
              } else { setLoginErr(res.message || "Failed"); }
            } catch (e) { setLoginErr(e.message); }
            finally { setLoading(false); }
          }}
          style={{ display: "flex", alignItems: "center", gap: sp.md, padding: sp.lg, borderRadius: rad.md, border: `1px solid ${h.bd}`, background: h.ib, cursor: loading ? "default" : "pointer", marginBottom: sp.sm, transition: "all 0.15s", opacity: loading ? 0.6 : 1 }}>
          <div style={{ width: 36, height: 36, borderRadius: rad.sm, background: `${h.ac}10`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Ic n="building" s={18} c={h.ac} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: h.tx, fontWeight: 600, fontSize: 14 }}>{tc.orgName}</div>
            <div style={{ color: h.ts, fontSize: 12, marginTop: 2 }}>{tc.propertyAddr}</div>
          </div>
          <Ic n="chevron-right" s={16} c={h.tt} />
        </div>)}
      </div>
    </div>

    : mode === "googleRole" ? <div style={{ width: "100%" }} className="animate-slideUp">
      <button onClick={() => { setMode("login"); setRole(null); setGoogleCredential(null); setGoogleUser(null); resetForm(); }} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: sp.md, fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}><Ic n="back" s={14} c={h.ac} /> {t.back}</button>
      <div style={{ background: h.cd, borderRadius: rad.lg, padding: sp.xxl, boxShadow: h.sh, border: `1px solid ${h.bd}` }}>
        {/* Google user info */}
        <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.lg }}>
          {googleUser?.avatar && <img src={googleUser.avatar} alt="" style={{ width: 40, height: 40, borderRadius: "50%" }} />}
          <div>
            <div style={{ color: h.tx, fontWeight: 700, fontSize: 16 }}>{googleUser?.name}</div>
            <div style={{ color: h.ts, fontSize: 12 }}>{googleUser?.email}</div>
          </div>
        </div>

        {!role ? <>
          <p style={{ color: h.ts, fontSize: 13, marginBottom: sp.lg, textAlign: "center" }}>{t.selectRole || "Select your role to complete signup"}</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.sm }}>
            {["landlord", "tenant", "service"].map(r => <button key={r} onClick={() => { setRole(r); setLoginErr(""); }} style={{ padding: "16px 8px", borderRadius: rad.md, border: `1px solid ${h.bd}`, background: h.ib, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: sp.sm }}>
              <div style={{ width: 36, height: 36, borderRadius: rad.sm, background: `${roleColors[r]}10`, display: "flex", alignItems: "center", justifyContent: "center" }}><Ic n={roleIcons[r]} s={18} c={roleColors[r]} /></div>
              <span style={{ color: h.tx, fontWeight: 600, fontSize: 11 }}>{roleLabels[r]}</span>
            </button>)}
          </div>
        </> : <>
          <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.lg }}>
            <div style={{ width: 32, height: 32, borderRadius: rad.sm, background: `${roleColors[role]}10`, display: "flex", alignItems: "center", justifyContent: "center" }}><Ic n={roleIcons[role]} s={16} c={roleColors[role]} /></div>
            <span style={{ color: h.tx, fontWeight: 600, fontSize: 14 }}>{roleLabels[role]}</span>
            <button onClick={() => setRole(null)} style={{ marginLeft: "auto", background: "none", border: "none", color: h.ts, cursor: "pointer", fontSize: 11 }}>Change</button>
          </div>

          {loginErr && <div style={{ background: `${h.rd}08`, color: h.rd, padding: "10px 14px", borderRadius: rad.md, fontSize: 13, fontWeight: 500, marginBottom: sp.lg, border: `1px solid ${h.rd}15` }}>{loginErr}</div>}

          <Fld l={t.yourName || "Name"} v={name} set={setName} h={h} ph={googleUser?.name || "Your full name"} />
          <Fld l={t.phone} v={ph} set={setPh} h={h} ph="+91 98765 43210" />
          {role === "landlord" && <Fld l={t.orgName || "Organization Name"} v={orgName} set={setOrgName} h={h} ph={"e.g. Rajieev Rentals"} />}
          {role === "service" && <Fld l={t.skill || "Skill"} v={skill} set={setSkill} h={h} ph={"e.g. Plumbing, Electrical"} />}

          {/* T&C + Privacy acceptance — required for Google signup too.
              Same component / same state as the regular signup form so
              the user only has to agree once. The server enforces this
              independently in /auth/google. */}
          <label style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "10px 4px", marginBottom: sp.sm, cursor: "pointer",
              userSelect: "none"
          }}>
              <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer", accentColor: h.ac, flexShrink: 0 }}
              />
              <span style={{ color: h.tx, fontSize: 12, lineHeight: 1.5 }}>
                  {t.iAccept || "I have read and agree to the"}{" "}
                  <span
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}
                      style={{ color: h.ac, textDecoration: "underline", fontWeight: 600 }}>
                      {t.termsAndConditions || "Terms & Conditions"}
                  </span>
                  {" "}and the{" "}
                  <span
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPrivacyModal(true); }}
                      style={{ color: h.ac, textDecoration: "underline", fontWeight: 600 }}>
                      {t.privacyPolicy || "Privacy Policy"}
                  </span>.
              </span>
          </label>

          <button
              disabled={loading || !termsAccepted}
              onClick={() => handleGoogleSignup(role)}
              style={{ ...btnP(h), opacity: (loading || !termsAccepted) ? 0.5 : 1, cursor: (loading || !termsAccepted) ? "not-allowed" : "pointer" }}>
            {loading ? "..." : (t.createAccount || "Create Account")}
          </button>
        </>}
      </div>
    </div>

    : !role ? <div style={{ width: "100%" }}>
      <p style={{ color: h.ts || h.sc, fontWeight: 500, marginBottom: sp.lg, textAlign: "center", fontSize: 13, letterSpacing: "0.3px", textTransform: "uppercase" }}>{t.selectRole}</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.md }}>
        {["landlord", "tenant", "service"].map(r => <button key={r} onClick={() => { setRole(r); resetForm(); setMode("login"); }} style={{ padding: "20px 8px", borderRadius: rad.lg, border: `1px solid ${h.bd}`, background: h.cd, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: sp.sm, boxShadow: h.sh, transition: "all 0.2s ease" }}>
          <div style={{ width: 44, height: 44, borderRadius: rad.md, background: `${roleColors[r]}10`, display: "flex", alignItems: "center", justifyContent: "center" }}><Ic n={roleIcons[r]} s={22} c={roleColors[r]} /></div>
          <span style={{ color: h.tx, fontWeight: 600, fontSize: 12 }}>{roleLabels[r]}</span>
        </button>)}
      </div>
      {/* Admin */}
      <button onClick={() => { setRole("admin"); resetForm(); setMode("login"); }} style={{ width: "100%", marginTop: sp.md, padding: "12px", borderRadius: rad.md, border: `1px solid ${h.bd}`, background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: sp.sm }}>
        <Ic n="gear" s={14} c={h.pp || h.wn} /><span style={{ color: h.pp || h.wn, fontWeight: 600, fontSize: 11 }}>{t.superAdmin || "Super Admin"}</span>
      </button>

      {/* Google Sign-In */}
      {GOOGLE_CLIENT_ID && <>
        <div style={{ display: "flex", alignItems: "center", gap: sp.md, margin: "28px 0 20px" }}>
          <div style={{ flex: 1, height: 1, background: h.bd }} />
          <span style={{ color: h.tt || h.sc, fontSize: 12, whiteSpace: "nowrap" }}>{t.orContinueWith || "or"}</span>
          <div style={{ flex: 1, height: 1, background: h.bd }} />
        </div>
        {googleLoading ? (
          <div style={{ textAlign: "center", padding: sp.lg }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
          </div>
        ) : (
          <div ref={googleBtnRef} style={{ display: "flex", justifyContent: "center", width: "100%" }} />
        )}
        {loginErr && <div style={{ background: `${h.rd}10`, color: h.rd, padding: "10px 14px", borderRadius: rad.md, fontSize: 13, fontWeight: 500, marginTop: sp.md, textAlign: "center", border: `1px solid ${h.rd}20` }}>{loginErr}</div>}
      </>}
    </div>

    : <div style={{ width: "100%" }} className="animate-slideUp">
      {loading ? (
        <div style={{ background: h.cd, borderRadius: rad.lg, padding: sp.xxxl, textAlign: "center", boxShadow: h.sh, border: `1px solid ${h.bd}` }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite", margin: "0 auto 16px", display: "block" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
          <p style={{ color: h.tx, fontWeight: 700, fontSize: 16, marginBottom: sp.sm }}>{mode === "signup" ? "Creating account..." : t.authenticating}</p>
          <p style={{ color: h.ts || h.sc, fontSize: 13, lineHeight: 1.5 }}>{t.connecting}<br/>{t.dontRefresh} <b style={{color: h.wn}}>50 {t.seconds}</b> {t.pleaseWait}</p>
        </div>
      ) : (
      <>
      <button onClick={() => { setRole(null); resetForm(); setMode("login"); }} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: sp.md, fontWeight: 500, display: "flex", alignItems: "center", gap: 4 }}><Ic n="back" s={14} c={h.ac} /> {t.back}</button>

      <div style={{ background: h.cd, borderRadius: rad.lg, padding: sp.xxl, boxShadow: h.sh, border: `1px solid ${h.bd}` }}>
        {/* Role header */}
        <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.lg }}>
          <div style={{ width: 40, height: 40, borderRadius: rad.md, background: `${roleColors[role] || h.ac}10`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Ic n={roleIcons[role] || "users"} s={20} c={roleColors[role] || h.ac} />
          </div>
          <div>
            <div style={{ color: h.tx, fontWeight: 700, fontSize: 16 }}>{roleLabels[role]}</div>
            <div style={{ color: h.ts || h.sc, fontSize: 12 }}>{mode === "signup" ? (t.createAccount || "Create Account") : t.login}</div>
          </div>
        </div>

        {/* Login / Sign Up tabs — not for admin */}
        {role !== "admin" && <div style={{ display: "flex", gap: sp.xs, background: h.ib || `${h.bd}`, borderRadius: rad.pill, padding: 3, marginBottom: sp.lg }}>
          <button onClick={() => switchMode("login")} style={tabS(mode === "login")}>{t.login}</button>
          <button onClick={() => switchMode("signup")} style={tabS(mode === "signup")}>{t.signUp || "Sign Up"}</button>
        </div>}

        {/* Error */}
        {loginErr && <div style={{ background: `${h.rd}08`, color: h.rd, padding: "10px 14px", borderRadius: rad.md, fontSize: 13, fontWeight: 500, marginBottom: sp.lg, border: `1px solid ${h.rd}15` }}>{loginErr}</div>}

        {/* === LOGIN FORM === */}
        {mode === "login" && !forgotMode && <>
          <Fld l={t.phone} v={ph} set={setPh} h={h} ph="+91 98765 43210" />
          <Fld l={t.password} v={pwd} set={setPwd} h={h} type="password" ph={t.enterPwd || "Enter password"} />
          <button disabled={loading} onClick={handleLogin} style={{ ...btnP(h), opacity: loading ? 0.7 : 1 }}>
            {loading ? "..." : t.login}
          </button>
          {role !== "admin" && <div style={{ textAlign: "center", marginTop: sp.sm }}>
            <span onClick={() => { setForgotMode(true); setForgotStep(1); setForgotErr(""); setForgotInfo(""); setForgotSuccess(false); setForgotPh(ph); setForgotOtp(""); setOtpResendIn(0); }}
              style={{ color: h.ac, fontSize: 12, cursor: "pointer", fontWeight: 500 }}>
              {t.forgotPassword || "Forgot Password?"}
            </span>
          </div>}
        </>}

        {/* === FORGOT PASSWORD FLOW === */}
        {mode === "login" && forgotMode && <div>
          {/* Back link */}
          <div onClick={() => { setForgotMode(false); setForgotErr(""); }} style={{ color: h.ac, fontSize: 12, cursor: "pointer", marginBottom: sp.lg, display: "inline-flex", alignItems: "center", gap: 4 }}>
            ← {t.backToLogin || "Back to Login"}
          </div>

          <div style={{ color: h.tx, fontWeight: 700, fontSize: 15, marginBottom: sp.xs }}>{t.resetPassword || "Reset Password"}</div>

          {/* Step indicators (2-step OTP flow) */}
          <div style={{ display: "flex", gap: sp.xs, marginBottom: sp.lg }}>
            {[1, 2].map(s => <div key={s} style={{ flex: 1, height: 3, borderRadius: 2, background: forgotStep >= s ? h.ac : h.bd, transition: "background 0.3s" }} />)}
          </div>

          {forgotSuccess ? <div style={{ textAlign: "center", padding: sp.xl }}>
            <div style={{ fontSize: 36, marginBottom: sp.md }}>✅</div>
            <div style={{ color: h.tx, fontWeight: 700, fontSize: 15, marginBottom: sp.xs }}>{t.passwordReset || "Password Reset!"}</div>
            <div style={{ color: h.ts, fontSize: 13, marginBottom: sp.lg }}>{t.canNowLogin || "You can now log in with your new password."}</div>
            <button onClick={() => { setForgotMode(false); setForgotErr(""); setForgotSuccess(false); }} style={btnP(h)}>{t.login}</button>
          </div> : <>
            {forgotErr && <div style={{ background: `${h.rd}08`, color: h.rd, padding: "10px 14px", borderRadius: rad.md, fontSize: 13, fontWeight: 500, marginBottom: sp.lg, border: `1px solid ${h.rd}15` }}>{forgotErr}</div>}
            {forgotInfo && <div style={{ background: `${h.gn || "#16a34a"}10`, color: h.gn || "#16a34a", padding: "10px 14px", borderRadius: rad.md, fontSize: 13, fontWeight: 500, marginBottom: sp.lg, border: `1px solid ${h.gn || "#16a34a"}25` }}>{forgotInfo}</div>}

            {/* Step 1: Phone → request OTP via WhatsApp */}
            {forgotStep === 1 && <>
              <div style={{ background: `${h.ac}08`, border: `1px solid ${h.ac}20`, borderRadius: rad.md, padding: sp.md, marginBottom: sp.lg, fontSize: 12, color: h.ts }}>
                We'll send a 6-digit OTP to your registered WhatsApp number. The code is valid for 10 minutes.
              </div>
              <Fld l={t.phone} v={forgotPh} set={setForgotPh} h={h} ph="+91 98765 43210" />
              <button disabled={loading} onClick={async () => {
                const trimmed = forgotPh.trim();
                if (!trimmed) { setForgotErr("Please enter your phone number"); return; }
                // Strict 10-digit Indian mobile after stripping non-digits
                const digits = trimmed.replace(/\D/g, '').replace(/^91/, '').replace(/^0+/, '');
                if (!/^[6-9]\d{9}$/.test(digits)) {
                  setForgotErr("Enter a valid 10-digit Indian mobile number");
                  return;
                }
                setLoading(true); setForgotErr("");
                const res = await api.requestPasswordResetOtp(role, trimmed);
                setLoading(false);
                // Server always returns 200 even for unknown phones (anti-enumeration).
                // Move user to step 2 either way.
                setForgotInfo(res.message || "If your number is registered, a WhatsApp OTP is on its way. It can take up to 30 seconds.");
                setForgotStep(2);
                setOtpResendIn(30);  // throttle resend client-side
              }} style={{ ...btnP(h), opacity: loading ? 0.7 : 1 }}>
                {loading ? "..." : (t.sendOtp || "Send OTP")}
              </button>
            </>}

            {/* Step 2: Enter OTP + new password */}
            {forgotStep === 2 && <>
              <div style={{ fontSize: 12, color: h.ts, marginBottom: sp.md }}>
                Sent to <b>{forgotPh}</b>. <span onClick={() => { setForgotStep(1); setForgotInfo(""); setForgotErr(""); }} style={{ color: h.ac, cursor: "pointer" }}>Change number</span>
              </div>
              <Fld l={t.otp || "OTP (6 digits)"} v={forgotOtp} set={setForgotOtp} h={h} type="text" ph="123456" />
              <Fld l={t.newPassword || "New Password"} v={forgotNewPwd} set={setForgotNewPwd} h={h} type="password" ph={t.enterPwd || "Min 8 characters"} />
              <Fld l={t.confirmPwd || "Confirm Password"} v={forgotConfirm} set={setForgotConfirm} h={h} type="password" ph={t.confirmPwd || "Re-enter password"} />
              <button disabled={loading} onClick={async () => {
                const otp = (forgotOtp || "").trim();
                if (!/^\d{6}$/.test(otp)) { setForgotErr("OTP must be exactly 6 digits"); return; }
                if (forgotNewPwd.length < 8) { setForgotErr("Password must be at least 8 characters"); return; }
                if (forgotNewPwd !== forgotConfirm) { setForgotErr("Passwords do not match"); return; }
                setLoading(true); setForgotErr("");
                const res = await api.verifyPasswordResetOtp(role, forgotPh.trim(), otp, forgotNewPwd);
                setLoading(false);
                if (res.success) {
                  setForgotInfo(""); setForgotSuccess(true);
                } else {
                  setForgotErr(res.message || "Invalid or expired OTP. Please try again.");
                }
              }} style={{ ...btnP(h), opacity: loading ? 0.7 : 1 }}>
                {loading ? "..." : (t.resetPassword || "Reset Password")}
              </button>
              {/* Resend OTP — throttled client-side; server still rate-limits */}
              <button disabled={otpResendIn > 0 || loading} onClick={async () => {
                setForgotErr(""); setLoading(true);
                const res = await api.requestPasswordResetOtp(role, forgotPh.trim());
                setLoading(false);
                setForgotInfo(res.message || "OTP resent. Check your WhatsApp.");
                setOtpResendIn(30);
                setForgotOtp("");
              }} style={{ ...btnSec(h), opacity: (otpResendIn > 0 || loading) ? 0.5 : 1, marginTop: sp.sm }}>
                {otpResendIn > 0 ? `Resend OTP in ${otpResendIn}s` : (t.resendOtp || "Resend OTP")}
              </button>
            </>}
          </>}
        </div>}

        {/* === SIGNUP FORM === */}
        {mode === "signup" && <>
          <Fld l={t.yourName || "Name"} v={name} set={setName} h={h} ph={t.yourName || "Your full name"} />
          {role === "landlord" && <Fld l={t.orgName || "Organization Name"} v={orgName} set={setOrgName} h={h} ph={"e.g. Rajieev Rentals"} />}
          <Fld l={t.phone} v={ph} set={setPh} h={h} ph="+91 98765 43210" />
          {role === "service" && <Fld l={t.skill || "Skill"} v={skill} set={setSkill} h={h} ph={"e.g. Plumbing, Electrical"} />}
          <Fld l={t.password} v={pwd} set={setPwd} h={h} type="password" ph={t.enterPwd || "Create password"} />
          <Fld l={t.confirmPwd || "Confirm Password"} v={confirmPwd} set={setConfirmPwd} h={h} type="password" ph={t.confirmPwd || "Confirm password"} />

          {/* Terms acceptance — must be checked before signup. The label
              text mirrors what the server records so consent language and
              UI language match exactly. */}
          <label style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              padding: "10px 4px", marginBottom: sp.sm, cursor: "pointer",
              userSelect: "none"
          }}>
              <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, cursor: "pointer", accentColor: h.ac, flexShrink: 0 }}
              />
              <span style={{ color: h.tx, fontSize: 12, lineHeight: 1.5 }}>
                  {t.iAccept || "I have read and agree to the"}{" "}
                  <span
                      onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                      style={{ color: h.ac, textDecoration: "underline", fontWeight: 600 }}>
                      {t.termsAndConditions || "Terms & Conditions"}
                  </span>
                  {" "}and the{" "}
                  <span
                      onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
                      style={{ color: h.ac, textDecoration: "underline", fontWeight: 600 }}>
                      {t.privacyPolicy || "Privacy Policy"}
                  </span>.
              </span>
          </label>

          <button
              disabled={loading || !termsAccepted}
              onClick={handleSignup}
              style={{ ...btnP(h), opacity: (loading || !termsAccepted) ? 0.5 : 1, cursor: (loading || !termsAccepted) ? "not-allowed" : "pointer" }}>
            {loading ? "..." : (t.createAccount || "Create Account")}
          </button>
        </>}
      </div>
      </>
      )}
    </div>
    }

    {/* Browse Properties CTA */}
    <button onClick={() => setPublicPage("browse")} style={{ marginTop: sp.xxl, width: "100%", padding: "13px 20px", borderRadius: rad.md, border: `1px solid ${h.bd}`, background: h.cd, color: h.tx, cursor: "pointer", fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", gap: sp.sm, boxShadow: h.sh, transition: "all 0.2s ease" }}>
      <Ic n="bldg" s={16} c={h.ac} />{t.browseProperties || "Browse Properties"}
    </button>
    <p style={{ color: h.tt || h.sc, fontSize: 11, marginTop: sp.lg }}>Version 4.0</p>

    {/* Terms & Conditions modal — opened from the signup checkbox. */}
    {showTermsModal && <TermsView onClose={() => setShowTermsModal(false)} />}
    {/* Privacy Policy modal — opened from the signup checkbox. */}
    {showPrivacyModal && <PrivacyView onClose={() => setShowPrivacyModal(false)} />}
  </div>;
}
